import { Link } from "react-router-dom";
import { HELP } from "@/lib/help-content";

interface HelpProps {
  /** Key into the HELP dictionary */
  k: string;
  /** Tooltip alignment relative to the icon */
  align?: "left" | "center" | "right";
}

/**
 * Small "?" icon that shows a tooltip on hover.
 * Use next to existing labels (stat cards, column headers).
 */
export function HelpIcon({ k, align = "left" }: HelpProps) {
  const entry = HELP[k];
  if (!entry) return null;

  return (
    <span className="group relative inline-flex ml-1 align-middle">
      <span className="inline-flex h-4 w-4 cursor-help items-center justify-center rounded-full border border-muted-foreground/40 text-[10px] font-medium text-muted-foreground transition-colors group-hover:border-primary group-hover:text-primary">
        ?
      </span>
      <TooltipPopup entry={entry} align={align} />
    </span>
  );
}

/**
 * Renders the term inline with a dotted underline and tooltip on hover.
 * Use when you want to replace the bare text with an annotated version.
 */
export function HelpTooltip({ k, align = "left" }: HelpProps) {
  const entry = HELP[k];
  if (!entry) return null;

  return (
    <span className="group relative inline">
      <span className="cursor-help border-b border-dotted border-muted-foreground/50 transition-colors group-hover:border-primary group-hover:text-primary">
        {entry.term}
      </span>
      <TooltipPopup entry={entry} align={align} />
    </span>
  );
}

function TooltipPopup({
  entry,
  align,
}: {
  entry: { term: string; brief: string; anchor: string };
  align: "left" | "center" | "right";
}) {
  const alignClass =
    align === "right"
      ? "right-0"
      : align === "center"
        ? "left-1/2 -translate-x-1/2"
        : "left-0";

  return (
    <span
      className={`pointer-events-none absolute top-full z-50 mt-1.5 w-72 rounded-lg border border-border bg-popover p-3 text-sm text-popover-foreground shadow-lg opacity-0 transition-opacity group-hover:pointer-events-auto group-hover:opacity-100 ${alignClass}`}
    >
      <span className="block font-semibold text-foreground mb-1">
        {entry.term}
      </span>
      <span className="block text-xs leading-relaxed text-muted-foreground">
        {entry.brief}
      </span>
      <Link
        to={`/guide#${entry.anchor}`}
        className="mt-2 block text-xs text-primary hover:underline"
      >
        See User Guide &rarr;
      </Link>
    </span>
  );
}
